import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Video, Copy, CheckCircle, Tv, Radio, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { LiveKitVideoWidget } from '../components/LiveKitVideo';

// Twitch channel for streaming
const TWITCH_CHANNEL = 'mestrecurtispierre';

// Get parent domain for Twitch embed
const getParentDomain = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') return 'localhost';
  return hostname;
};

/**
 * LiveKit Video Room - Client joins here
 * Uses LiveKit Cloud for production-grade stability
 * OR Twitch stream for clients with slow connections
 */
const LiveKitRoom = () => {
  const { roomCode } = useParams();
  const location = useLocation();
  
  // Detect if host from URL
  const isHost = location.pathname.endsWith('/host');
  
  const [roomId, setRoomId] = useState('');
  const [joined, setJoined] = useState(false);
  const [copied, setCopied] = useState(false);
  const [username, setUsername] = useState('');
  const [connectionMode, setConnectionMode] = useState('livekit'); // 'livekit' or 'twitch'

  useEffect(() => {
    if (roomCode) {
      setRoomId(roomCode.toUpperCase());
    } else {
      const newCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      setRoomId(newCode);
      window.history.replaceState({}, '', `/livekit-room/${newCode}/host`);
    }
  }, [roomCode]);

  const copyClientLink = () => {
    const link = `${window.location.origin}/livekit-room/${roomId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Client link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  const handleJoin = () => {
    if (!username.trim()) {
      toast.error('Please enter your name');
      return;
    }
    setJoined(true);
  };

  if (joined) {
    // Twitch Stream Mode - Watch only (for slow connections)
    if (connectionMode === 'twitch') {
      return (
        <div className="min-h-screen bg-black">
          {/* Twitch Stream Embed - Full Screen */}
          <div className="absolute inset-0">
            <iframe
              src={`https://player.twitch.tv/?channel=${TWITCH_CHANNEL}&parent=${getParentDomain()}&autoplay=true`}
              className="w-full h-full"
              allowFullScreen
              frameBorder="0"
              title="Escola de Samba Casa Samba Live Stream"
            />
          </div>
          
          {/* Overlay Info */}
          <div className="fixed top-4 left-4 z-10 bg-purple-600/90 backdrop-blur px-4 py-2 rounded-lg flex items-center gap-2">
            <Tv className="w-5 h-5 text-white" />
            <span className="text-white font-bold">Watching Twitch Stream</span>
          </div>
          
          {/* Chat/Controls Panel */}
          <div className="fixed bottom-4 right-4 z-10 bg-gray-900/90 backdrop-blur rounded-xl p-4 w-80">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-bold">Room: {roomId}</span>
              <span className="text-purple-400 text-sm">{username}</span>
            </div>
            <p className="text-gray-400 text-sm mb-3">
              You're watching the host's stream. For two-way video, use a faster connection.
            </p>
            <button
              onClick={() => setConnectionMode('livekit')}
              className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold text-sm flex items-center justify-center gap-2"
            >
              <Video className="w-4 h-4" />
              Switch to Two-Way Video
            </button>
          </div>
          
          {/* Twitch Chat Embed */}
          <div className="fixed bottom-4 left-4 z-10 w-80 h-96 bg-gray-900 rounded-xl overflow-hidden">
            <iframe
              src={`https://www.twitch.tv/embed/${TWITCH_CHANNEL}/chat?parent=${getParentDomain()}&darkpopout`}
              className="w-full h-full"
              frameBorder="0"
              title="Twitch Chat"
            />
          </div>
        </div>
      );
    }
    
    // LiveKit Mode - Two-way video
    return (
      <div className="min-h-screen bg-black relative">
        {/* Full screen area for receiving screen share */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-2xl font-bold mb-2">Escola de Samba Casa Samba Live Session</h1>
            <p className="text-gray-400 mb-1">Room: {roomId}</p>
            <p className="text-gray-500 text-sm">
              {isHost ? 'Click the screen share button to share your DAW' : 'Waiting for host to share screen...'}
            </p>
            <p className="text-gray-600 text-xs mt-4">Video controls in bottom-right corner</p>
            
            {/* Switch to Twitch option for clients */}
            {!isHost && (
              <button
                onClick={() => setConnectionMode('twitch')}
                className="mt-6 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white text-sm flex items-center gap-2 mx-auto"
              >
                <Tv className="w-4 h-4" />
                Slow connection? Watch Twitch stream instead
              </button>
            )}
          </div>
        </div>
        
        {/* LiveKit widget - positioned for client to see screen share */}
        <LiveKitVideoWidget 
          roomCode={roomId}
          username={username}
          isHost={isHost}
          minimizable={true}
          fullscreenScreenShare={!isHost} // Client gets fullscreen screen share view
        />
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isHost ? 'bg-gradient-to-br from-green-900 via-black to-green-900' : 'bg-gradient-to-br from-blue-900 via-black to-blue-900'}`}>
      <div className="bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full border-2 border-gray-600">
        
        {/* Role indicator */}
        <div className={`text-center py-4 rounded-2xl mb-6 ${isHost ? 'bg-green-600' : 'bg-blue-600'}`}>
          <span className="text-4xl mb-2 block">{isHost ? '👑' : '👤'}</span>
          <span className="text-2xl font-black text-white">
            {isHost ? 'HOST' : 'CLIENT'}
          </span>
        </div>
        
        <div className="flex items-center justify-center gap-3 mb-6">
          <Video className="w-8 h-8 text-green-500" />
          <h1 className="text-2xl font-bold text-white">Escola de Samba Casa Samba Studio</h1>
        </div>
        
        <p className="text-center text-gray-400 text-sm mb-6">
          Powered by LiveKit - Production-grade video
        </p>
        
        <div className="bg-black/50 rounded-xl p-4 mb-4">
          <p className="text-gray-400 text-sm">Room Code</p>
          <p className="text-3xl font-mono font-bold text-yellow-400">{roomId || 'Loading...'}</p>
        </div>
        
        <div className="mb-4">
          <label className="text-gray-400 text-sm block mb-2">Your Name</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your name"
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white focus:border-green-500 focus:outline-none"
          />
        </div>
        
        {isHost && (
          <div className="bg-green-900/50 border-2 border-green-500 rounded-xl p-4 mb-4">
            <p className="text-green-300 font-bold mb-2">📤 Send this to your CLIENT:</p>
            <div className="bg-black rounded p-2 mb-2">
              <code className="text-green-400 text-sm break-all">
                {window.location.origin}/livekit-room/{roomId}
              </code>
            </div>
            <button
              onClick={copyClientLink}
              className="w-full py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-bold flex items-center justify-center gap-2"
            >
              {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              {copied ? 'Copied!' : 'Copy Client Link'}
            </button>
          </div>
        )}
        
        {!isHost && (
          <>
            <div className="bg-gray-900/50 rounded-xl p-4 mb-4">
              <p className="text-gray-400 text-sm mb-3">Connection Mode:</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConnectionMode('livekit')}
                  className={`flex-1 py-3 rounded-lg font-bold text-sm flex flex-col items-center gap-1 transition-all ${
                    connectionMode === 'livekit'
                      ? 'bg-green-600 text-white ring-2 ring-green-400'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <Wifi className="w-5 h-5" />
                  <span>Two-Way Video</span>
                  <span className="text-[10px] opacity-70">Good connection</span>
                </button>
                <button
                  onClick={() => setConnectionMode('twitch')}
                  className={`flex-1 py-3 rounded-lg font-bold text-sm flex flex-col items-center gap-1 transition-all ${
                    connectionMode === 'twitch'
                      ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <Tv className="w-5 h-5" />
                  <span>Watch Stream</span>
                  <span className="text-[10px] opacity-70">Slow connection</span>
                </button>
              </div>
            </div>
          </>
        )}
        
        <button
          onClick={handleJoin}
          disabled={!roomId || !username.trim()}
          className={`w-full py-4 font-bold text-xl rounded-xl text-white ${
            isHost 
              ? 'bg-green-600 hover:bg-green-500' 
              : connectionMode === 'twitch'
                ? 'bg-purple-600 hover:bg-purple-500'
                : 'bg-blue-600 hover:bg-blue-500'
          } disabled:opacity-50`}
        >
          {isHost ? 'JOIN AS HOST 👑' : connectionMode === 'twitch' ? 'WATCH STREAM 📺' : 'JOIN VIDEO CALL 👤'}
        </button>
      </div>
    </div>
  );
};

export default LiveKitRoom;
